import { useEffect, useState } from "react";
import type { ContactPreviewOutcome } from "@/lib/api/organizations";

const ADDRESS_CHECK_DELAY_MS = 400;

export const looksLikeEmail = (value: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

/// Asks `check` about `email` 400 ms after it settles, while `enabled` and it
/// is shaped like an address; otherwise the verdict is cleared.
export function useAddressCheck(
  email: string,
  enabled: boolean,
  check: (email: string) => Promise<ContactPreviewOutcome | null>,
): { verdict: ContactPreviewOutcome | null; checking: boolean } {
  const [verdict, setVerdict] = useState<ContactPreviewOutcome | null>(null);
  const [checking, setChecking] = useState(false);
  const usable = enabled && looksLikeEmail(email);

  useEffect(() => {
    if (!usable) {
      setVerdict(null);
      setChecking(false);
      return;
    }
    setChecking(true);
    let live = true;
    const timer = window.setTimeout(() => {
      void check(email).then((outcome) => {
        if (!live) return;
        setVerdict(outcome);
        setChecking(false);
      });
    }, ADDRESS_CHECK_DELAY_MS);
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [usable, email, check]);

  return { verdict, checking };
}
